import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Form,
  Label,
  Input,
  Button,
  Alert,
  Spinner
} from 'reactstrap';
import { BRAND_ADMIN_FULL_NAME, BRAND_ADMIN_LOGIN_TAGLINE } from '../constants/brand';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <React.Fragment>
      <div className="auth-page-wrapper pt-5">
        <div className="auth-one-bg-position auth-one-bg" id="auth-particles">
          <div className="bg-overlay"></div>
          <div className="shape">
            <svg xmlns="http://www.w3.org/2000/svg" version="1.1" viewBox="0 0 1440 120">
              <path d="M 0,36 C 144,53.6 432,123.2 720,124 C 1008,124.8 1296,56.8 1440,40L1440 140L0 140z"></path>
            </svg>
          </div>
        </div>

        <div className="auth-page-content">
          <Container>
            <Row>
              <Col lg={12}>
                <div className="text-center mt-sm-5 mb-5 text-white-50">
                  <div className="mb-3">
                    <div className="d-inline-block auth-logo">
                      <h2 className="text-white fw-bold" style={{ 
                        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' 
                      }}>
                        {BRAND_ADMIN_FULL_NAME}
                      </h2>
                    </div>
                  </div>
                  <p className="mt-2 fs-15 fw-medium">{BRAND_ADMIN_LOGIN_TAGLINE}</p>
                </div>
              </Col>
            </Row>

            <Row className="justify-content-center">
              <Col md={8} lg={6} xl={5}>
                <Card className="mt-4 border-0 shadow-lg" style={{ 
                  borderRadius: '16px',
                  overflow: 'hidden',
                  backdropFilter: 'blur(10px)',
                  backgroundColor: 'rgba(255, 255, 255, 0.98)'
                }}>
                  <CardBody className="p-5">
                    <div className="text-center mb-4">
                      <div className="avatar-lg mx-auto mb-3" style={{
                        width: '72px',
                        height: '72px',
                        borderRadius: '16px',
                        background: 'linear-gradient(135deg, #405189 0%, #5ea3cb 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 8px 16px rgba(64, 81, 137, 0.3)'
                      }}>
                        <i className="ri-user-line fs-1 text-white"></i>
                      </div>
                      <h4 className="text-dark fw-bold mb-2">Welcome Back!</h4>
                      <p className="text-muted mb-0">Enter your credentials to continue</p>
                    </div>

                    {error && (
                      <Alert color="danger" className="border-0 rounded-3 mb-4" style={{ 
                        backgroundColor: '#fee',
                        borderLeft: '4px solid #dc3545'
                      }}>
                        <i className="ri-error-warning-line me-2"></i>
                        {error}
                      </Alert>
                    )}

                    <Form onSubmit={handleSubmit}>
                      <div className="mb-4">
                        <Label htmlFor="email-input" className="form-label fw-semibold text-dark mb-2">
                          <i className="ri-mail-fill me-2 text-primary"></i>
                          Email Address
                        </Label>
                        <Input
                          type="email"
                          className="form-control"
                          id="email-input"
                          placeholder="Enter your email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                          style={{
                            padding: '0.75rem 1rem',
                            fontSize: '0.9375rem',
                            borderRadius: '12px',
                            border: '2px solid #e9ecef',
                            transition: 'all 0.3s ease'
                          }}
                        />
                      </div>

                      <div className="mb-3">
                      <div className="float-end">
                        <button 
                          type="button"
                          className="text-muted text-decoration-none bg-transparent border-0 p-0" 
                          style={{ fontSize: '0.8125rem', cursor: 'pointer' }}
                          onClick={(e) => e.preventDefault()}
                        >
                          Forgot password?
                        </button>
                      </div>
                        <Label className="form-label fw-semibold text-dark mb-2" htmlFor="password-input">
                          <i className="ri-lock-password-line me-2 text-primary"></i>
                          Password
                        </Label>
                        <Input
                          type="password"
                          className="form-control"
                          id="password-input"
                          placeholder="Enter your password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          style={{
                            padding: '0.75rem 1rem',
                            fontSize: '0.9375rem',
                            borderRadius: '12px',
                            border: '2px solid #e9ecef',
                            transition: 'all 0.3s ease'
                          }}
                        />
                      </div>

                      <div className="form-check mb-4">
                        <Input className="form-check-input" type="checkbox" value="" id="auth-remember-check" />
                        <Label className="form-check-label" htmlFor="auth-remember-check" style={{ fontSize: '0.875rem' }}>
                          Remember me
                        </Label>
                      </div>

                      <div className="mt-4">
                        <Button 
                          color="primary" 
                          className="w-100 fw-semibold" 
                          type="submit" 
                          disabled={loading}
                          style={{
                            padding: '0.875rem',
                            fontSize: '1rem',
                            borderRadius: '12px',
                            background: 'linear-gradient(135deg, #405189 0%, #5ea3cb 100%)',
                            border: 'none',
                            boxShadow: '0 8px 20px rgba(64, 81, 137, 0.4)',
                            transition: 'all 0.3s ease',
                            position: 'relative'
                          }}
                        >
                          {loading ? (
                            <>
                              <Spinner size="sm" className="me-2" />
                              Signing in...
                            </>
                          ) : (
                            <>
                              <i className="ri-login-circle-line me-2"></i>
                              Sign In
                            </>
                          )}
                        </Button>
                      </div>
                    </Form>
                    <p className="text-center text-muted small mb-0 mt-4">
                      <Link to="/privacy" className="text-muted text-decoration-none">
                        Privacy Policy
                      </Link>
                      <span className="mx-2 text-muted">·</span>
                      <Link to="/terms" className="text-muted text-decoration-none">
                        Terms of Use
                      </Link>
                    </p>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>

        <footer className="footer start-0">
          <Container>
            <Row>
              <Col lg={12}>
                <div className="text-center">
                  <p className="mb-2">
                    <Link to="/privacy" className="text-white-50 text-decoration-none me-3">
                      Privacy Policy
                    </Link>
                    <Link to="/terms" className="text-white-50 text-decoration-none">
                      Terms of Use
                    </Link>
                  </p>
                  <p className="mb-0 text-white-50">
                    &copy; {new Date().getFullYear()} {BRAND_ADMIN_FULL_NAME}. All rights reserved.
                  </p>
                </div>
              </Col>
            </Row>
          </Container>
        </footer>
      </div>
    </React.Fragment>
  );
};

export default Login;
